const API_URL = process.env.REACT_APP_API_URL || '/api';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.token = null;
    this.isConnecting = false; // prevents connect() from racing with an existing CONNECTING
    this._wasEverConnected = false; // set on first ever connect; stays true for reconnect detection
    this._callResumeInFlight = false; // debounces call_resume send during rapid reconnects
    this._pendingSends = []; // queued {type, payload} while reconnecting
    this._reconnectAttempts = 0; // track reconnect attempts for exponential backoff
    this._maxReconnectAttempts = 5; // mobile: limit to prevent battery drain
    this._baseReconnectDelay = 1000; // start with 1s delay
  }

  connect(token) {
    // Prevent duplicate WebSocket connections during React StrictMode remount
    // or multiple rapid connect() calls.
    // If already connected or connecting, do nothing.
    if (this.ws) {
      const state = this.ws.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }
      // If closing or closed, clean up the old ws
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    // Double-check after nulling ws — another thread might have created one
    if (this.isConnecting) {
      return;
    }
    this.isConnecting = true;

    // Preserve token so waitForOpen can still reconnect after onclose nulls it
    this.token = token;
    this.savedToken = token;
    
    // Build WebSocket URL
    // Use REACT_APP_WS_URL if set (e.g., ws://localhost:8080 for local dev with direct backend)
    // Otherwise, use Apache proxy via same host/port (for production behind Apache)
    let url;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const configuredUrl = process.env.REACT_APP_WS_URL;
    
    if (configuredUrl) {
      // Use configured URL (e.g., ws://localhost:8080 for local dev)
      url = `${configuredUrl}/api/ws?token=${token}`;
    } else {
      // Use Apache proxy - Apache handles WS proxying to backend
      // Remove any explicit port so it uses default 443 for wss
      const wsHost = window.location.host.replace(/:\d+$/, '');
      url = `${wsProtocol}//${wsHost}/api/ws?token=${token}`;
    }
    console.log('[WS] Connecting to:', url);

    try {
      this.isConnecting = true;
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      this.isConnecting = false;
      return;
    }

    this.ws.onopen = () => {
      this.isConnecting = false;
      // Track whether we've *ever* had a connection — used to distinguish reconnect
      // from a fresh connect when deciding whether to send call_resume.
      // Only set on first ever connect; reconnects keep it true.
      if (!this._wasEverConnected) {
        this._wasEverConnected = true;
      }
      console.log('[WS] onopen fired, readyState:', this.ws.readyState, 'activeCallId:', this._activeCallId);
      this.notifyListeners('connected', null);
      // Flush any pending sends queued while WS was reconnecting
      if (this._pendingSends.length > 0) {
        console.log('[WS] flushing', this._pendingSends.length, 'pending messages');
        for (const { type, payload } of this._pendingSends) {
          this.ws.send(JSON.stringify({ type, payload }));
        }
        this._pendingSends = [];
      }
      // Send call_resume only when reconnecting after a genuine connection.
      // First connect (fresh app mount) should NOT send call_resume even if a callId
      // was previously stored — the call_resume mechanism handles ongoing calls
      // when the WS abnormally closes mid-call.
      if (this._activeCallId && !this._callResumeInFlight && this._wasEverConnected) {
        console.log('[WS] sending call_resume for:', this._activeCallId);
        this._callResumeInFlight = true;
        this.send('call', { event: 'call_resume', data: { call_id: this._activeCallId } });
        // Clear the flag after 3s in case the server never responds (stale callId scenario)
        setTimeout(() => { this._callResumeInFlight = false; }, 3000);
      }
      if (this._onReconnect) {
        try { this._onReconnect(); } catch (_) {}
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[WS] Received message type:', message.type, 'payload:', message.payload);
        this.notifyListeners(message.type, message.payload);
      } catch (err) {
        console.error('Failed to parse WS message:', err);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onclose = (event) => {
      console.log('[WS] onclose: code=', event.code, 'reason=', event.reason, 'wasClean=', event.wasClean, 'readyState=', this.ws?.readyState);
      // Don't null token — savedToken preserves the token for reconnect attempts
      this.token = null;
      this.isConnecting = false;
      this.notifyListeners('disconnected', null);
      // attemptReconnect removed — reconnect is driven by the useEffect re-mounting ChatContext
    };
  }

  disconnect() {
    this.token = null;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Register a callback to be called when WS reconnects (after onopen following a disconnect).
   * Used by ChatContext to re-register an active call session.
   */
  onReconnect(cb) {
    this._onReconnect = cb;
  }

  /**
   * Track the active call session so the reconnect handler can resume it.
   * Called by ChatContext when a call starts/ends.
   */
  setActiveCallId(callId) {
    this._activeCallId = callId;
  }

  /**
   * Send a message over WebSocket.
   * If connection is not open, queues the message and triggers reconnect.
   * Returns true if sent immediately, false if queued.
   */
  send(type, payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WS] Sending message type:', type, 'payload:', payload);
      this.ws.send(JSON.stringify({ type, payload }));
      return true;
    }
    // Connection not open — queue this message and reconnect
    console.log('[WS] Send queued (WS not open), type:', type);
    this._pendingSends.push({ type, payload });
    const tok = this.token || this.savedToken;
    if (tok) {
      this.connect(tok);
    }
    return false;
  }

  /**
   * Send a message and wait for it to be actually sent.
   * If WS is not open, waits for reconnection and flushes pending messages.
   * Returns a promise that resolves when the message is sent.
   */
  sendAndWait(type, payload, timeoutMs = 10000) {
    // If already open, send immediately and resolve
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WS] sendAndWait: sending immediately, type:', type);
      this.ws.send(JSON.stringify({ type, payload }));
      return Promise.resolve();
    }

    // Queue the message
    console.log('[WS] sendAndWait: queuing message, type:', type);
    this._pendingSends.push({ type, payload });

    // Start connection if needed
    const tok = this.token || this.savedToken;
    if (tok && (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING)) {
      this.connect(tok);
    }

    // Wait for connection and flush
    return new Promise((resolve, reject) => {
      let timeout = setTimeout(() => {
        reject(new Error('sendAndWait timeout'));
      }, timeoutMs);

      const checkAndFlush = () => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          // Flush all pending messages
          while (this._pendingSends.length > 0) {
            const pending = this._pendingSends.shift();
            console.log('[WS] sendAndWait: flushing pending message', pending.type);
            this.ws.send(JSON.stringify({ type: pending.type, payload: pending.payload }));
          }
          clearTimeout(timeout);
          resolve();
        } else if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
          // Connection failed, try to reconnect with exponential backoff
          const tok = this.token || this.savedToken;
          if (tok && this._reconnectAttempts < this._maxReconnectAttempts) {
            this._reconnectAttempts++;
            // Calculate delay with exponential backoff (1s, 2s, 4s, 8s, 16s)
            const delay = Math.min(this._baseReconnectDelay * Math.pow(2, this._reconnectAttempts - 1), 16000);
            console.log('[WS] sendAndWait: reconnect attempt', this._reconnectAttempts, 'with delay', delay, 'ms');
            this.connect(tok);
            setTimeout(checkAndFlush, delay);
          } else {
            clearTimeout(timeout);
            this._reconnectAttempts = 0; // Reset on failure
            reject(new Error('sendAndWait: max reconnect attempts reached or no token'));
          }
        } else {
          // Still connecting, keep polling
          setTimeout(checkAndFlush, 100);
        }
      };

      checkAndFlush();
    });
  }

  /**
   * Returns a promise that resolves when the WebSocket is OPEN,
   * or resolves immediately if already open. Times out after 10s.
   */
  waitForOpen(timeoutMs = 10000) {
    // If the WS is closed or closing, try to reconnect if we have a token.
    // Use savedToken since onclose may have nulled this.token.
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
      const tok = this.token || this.savedToken;
      if (tok) {
        this.connect(tok);
      }
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      let rejectTimer = setTimeout(() => {
        running = false;
        reject(new Error('WS waitForOpen timeout'));
      }, timeoutMs);

      let running = true;
      const check = () => {
        if (!running) return;
        if (!this.ws) {
          running = false;
          clearTimeout(rejectTimer);
          reject(new Error('WS closed while waiting'));
          return;
        }
        switch (this.ws.readyState) {
          case WebSocket.OPEN:
            running = false;
            clearTimeout(rejectTimer);
            resolve();
            break;
          case WebSocket.CLOSED:
          case WebSocket.CLOSING:
            running = false;
            clearTimeout(rejectTimer);
            // One reconnect attempt before giving up
            const tok = this.token || this.savedToken;
            if (tok) {
              this.connect(tok);
              // Re-poll after 300ms
              setTimeout(() => {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                  resolve();
                } else {
                  reject(new Error('WS closed while waiting'));
                }
              }, 300);
            } else {
              reject(new Error('WS closed while waiting'));
            }
            break;
          // CONNECTING: keep polling
          default:
            setTimeout(check, 100);
        }
      };
      check();
    });
  }

  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  notifyListeners(eventType, payload) {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(payload);
        } catch (err) {
          console.error(`WebSocket listener error for ${eventType}:`, err);
        }
      });
    }
  }
}

const websocketService = new WebSocketService();
window.__ws = websocketService; // expose for test debugging
export default websocketService;
